import {useEffect, useMemo, useState} from 'react';
import {DateColumn, DateTimeColumn, OpenColumn, RichtextColumn, SelectColumn, UserDtoColumn, BreakWordColumn} from '../../common/EnhancedDetailsList';
import {ColumnKey, IEnhancedColumn} from '../../common/EnhancedDetailsList/IEnhancedDetailsList';
import {OpenPDCColumn} from '../../common/EnhancedDetailsList/ColumnTypes/OpenPDCColumn';
import {MyTask} from '../../../models/MyTask';
import {useUser} from '../../../hooks/services/useUser';
import {MyTaskType} from '../../../services/generated/models/MyTaskType';
import { useAcknowledgementBatch } from '../../../hooks/services/useAcknowledgementBatch';
import { MyAcknowledgementModel } from '../../../models/MyAcknowledgementModel';

export const useMyTask = (isOpenModal: boolean) => {
  const {getUserMyTasks, getUserHasAccessToAdditionalTabs} = useUser();
  const {getMyInitiatedAcknowledgement,getReceivedAcknowledgement} = useAcknowledgementBatch()

  const [isProcessExpiredLoading, setIsProcessExpiredLoading] = useState(false);
  const [isInitiatedAcknowledgementsLoading, setIsInitiatedAcknowledgementsLoading] = useState(false);
  const [isReceivedAcknowledgementsLoading, setIsReceivedAcknowledgementsLoading] = useState(false);
  const [isDocumentExpiredLoading, setIsDocumentExpiredLoading] = useState(false);
  const [isDocumentAwaitingApprovalLoading, setIsDocumentAwaitingApprovalLoading] = useState(false);
  const [isUnresolvedFeedbackLoading, setIsUnresolvedFeedbackLoading] = useState(false);

  const [initiatedAcknowledgements, setInitiatedAcknowledgements] = useState<MyAcknowledgementModel[]>([]);
  const [receivedAcknowledgements, setReceivedAcknowledgements] = useState<MyAcknowledgementModel[]>([]);
  const [processExpiredResult, setProcessExpiredResult] = useState<MyTask[]>([]);
  const [documentExpiredResult, setDocumentExpiredResult] = useState<MyTask[]>([]);
  const [documentAwaitingApprovalResult, setDocumentAwaitingApprovalResult] = useState<MyTask[]>(
    []
  );
  const [unresolvedFeedbackResult, setUnresolvedFeedbackResult] = useState<MyTask[]>([]);
  const [userHasAccessToAdditionalTabs, setUserHasAccessToAdditionalTabs] = useState(false);

  const columnProcessExpired = useMemo(() => {
    const cols: IEnhancedColumn<MyTask>[] = [
      {
        key: ColumnKey.Url,
        name: '',
        data: OpenColumn
      },
      {
        key: ColumnKey.Title,
        name: 'Title',
        minWidth: 420,
        flexGrow: 1
      },
      {
        key: ColumnKey.ExpiredDate,
        name: 'Expired Date',
        data: DateColumn,
        minWidth: 130
      }
    ];
    return cols;
  }, []);

  const columnsDocumentExpired = useMemo(() => {
    const cols: IEnhancedColumn<MyTask>[] = [
      {
        key: ColumnKey.Url,
        name: '',
        data: OpenPDCColumn
      },
      {
        key: ColumnKey.Title,
        name: 'Title',
        minWidth: 420,
        data: BreakWordColumn,
        flexGrow: 1
      },

      {
        key: ColumnKey.ExpiredDate,
        name: 'Expired Date',
        data: DateColumn,
        minWidth: 130
      },
      {
        key: ColumnKey.DocumentId,
        name: 'DocumentId',
        minWidth: 135,
        flexGrow: 1
      },
      {
        key: ColumnKey.Language,
        name: 'Language',
        minWidth: 120,
        flexGrow: 1,
        data: SelectColumn
      },
      {
        key: ColumnKey.ContentTypeString,
        name: 'Content Type',
        data: SelectColumn,
        minWidth: 135,
        exportToExcel: true
      }
    ];
    return cols;
  }, []);

  const columnsDocumentsAwaitngApproval = useMemo(() => {
    const cols: IEnhancedColumn<MyTask>[] = [
      {
        key: ColumnKey.Url,
        name: '',
        data: OpenColumn
      },
      {
        key: ColumnKey.Title,
        name: 'Title',
        minWidth: 420,
        flexGrow: 1
      },

      {
        key: ColumnKey.ExpiredDate,
        name: 'Due Date',
        data: DateColumn,
        minWidth: 165
      }
    ];
    return cols;
  }, []);

  const columnUnresolvedFeedback = useMemo(() => {
    const cols: IEnhancedColumn<MyTask>[] = [
      {
        key: ColumnKey.Url,
        name: '',
        data: OpenColumn
      },
      {
        key: ColumnKey.Title,
        name: 'Process Title',
        minWidth: 300,
        flexGrow: 1
      },
      {
        key: ColumnKey.Created,
        name: 'Created',
        data: DateTimeColumn,
        minWidth: 165
      },
      {
        key: ColumnKey.CreatedBy,
        name: 'Created by',
        data: UserDtoColumn,
        minWidth: 250
      },
      
      {
        key: ColumnKey.Content,
        name: 'Comment',
        data: RichtextColumn,
        minWidth: 460
      }
    ];
    return cols;
  }, []);

  


  useEffect(() => {
    (async () => {
      if (!isOpenModal) return;
      var hasAccessToAdditionalTabs = await getUserHasAccessToAdditionalTabs();
      if (hasAccessToAdditionalTabs.result) {
        setUserHasAccessToAdditionalTabs(hasAccessToAdditionalTabs.result);
      }
    })();
  }, [getUserHasAccessToAdditionalTabs,isOpenModal]);

  useEffect(() => {
    (async () => {
      setIsInitiatedAcknowledgementsLoading(true);
      if (!isOpenModal || !userHasAccessToAdditionalTabs) return;
      const response = await getMyInitiatedAcknowledgement();
      if(response.result) {
        const result = response.result.sort((a, b) => (a.created < b.created ? 1 : -1));
         setInitiatedAcknowledgements(result);
      }
      setIsInitiatedAcknowledgementsLoading(false);
    
    })();
  }, [setIsInitiatedAcknowledgementsLoading,getMyInitiatedAcknowledgement,userHasAccessToAdditionalTabs, isOpenModal]);

   useEffect(() => {
    (async () => {
      if (!isOpenModal) return;
      setIsReceivedAcknowledgementsLoading(true);
      const response = await getReceivedAcknowledgement();
       if(response.result) {
        const result = response.result.sort((a, b) => (a.created < b.created ? 1 : -1)); 
         setReceivedAcknowledgements(result);
      }
      setIsReceivedAcknowledgementsLoading(false);
    
    })();
  }, [setIsInitiatedAcknowledgementsLoading,getReceivedAcknowledgement, isOpenModal]);

  useEffect(() => {
    (async () => {
      if (!isOpenModal) return;
      setIsProcessExpiredLoading(true);

      const results = await getUserMyTasks(MyTaskType.ProcessExpired);
      setProcessExpiredResult(results.result || []);
      setIsProcessExpiredLoading(false);
    })();
  }, [setIsProcessExpiredLoading, getUserMyTasks, isOpenModal]);

  useEffect(() => {
    (async () => {
      if (!isOpenModal) return;
      setIsDocumentExpiredLoading(true);

      const results = await getUserMyTasks(MyTaskType.DocumentExpired);
      setDocumentExpiredResult(results.result || []);
      setIsDocumentExpiredLoading(false);
    })();
  }, [setIsDocumentExpiredLoading, getUserMyTasks, isOpenModal]);

  useEffect(() => {
    (async () => {
      if (!isOpenModal) return;
      setIsUnresolvedFeedbackLoading(true);

      const results = await getUserMyTasks(MyTaskType.UnresolvedFeedback);
      setUnresolvedFeedbackResult(results.result || []);
      setIsUnresolvedFeedbackLoading(false);
    })();
  }, [setIsUnresolvedFeedbackLoading, getUserMyTasks, isOpenModal]);

  useEffect(() => {
    (async () => {
      if (!isOpenModal) return;
      setIsDocumentAwaitingApprovalLoading(true);

      const results = await getUserMyTasks(MyTaskType.DocumentAwaitingApproval);
      setDocumentAwaitingApprovalResult(results.result || []);
      setIsDocumentAwaitingApprovalLoading(false);
    })();
  }, [setIsDocumentAwaitingApprovalLoading, getUserMyTasks, isOpenModal]);

  return useMemo(
    () => ({
      columnProcessExpired,
      columnsDocumentExpired,
      columnsDocumentsAwaitngApproval,
      columnUnresolvedFeedback,
      processExpiredResult,
      documentExpiredResult,
      documentAwaitingApprovalResult,
      unresolvedFeedbackResult,
      isProcessExpiredLoading,
      isDocumentExpiredLoading,
      isDocumentAwaitingApprovalLoading,
      isInitiatedAcknowledgementsLoading,
      isReceivedAcknowledgementsLoading,
      userHasAccessToAdditionalTabs,
      isUnresolvedFeedbackLoading,
      initiatedAcknowledgements,
      receivedAcknowledgements,
    }),
    [
      columnProcessExpired,
      columnsDocumentExpired,
      columnsDocumentsAwaitngApproval,
      columnUnresolvedFeedback,
      processExpiredResult,
      documentExpiredResult,
      documentAwaitingApprovalResult,
      unresolvedFeedbackResult,
      isProcessExpiredLoading,
      isDocumentExpiredLoading,
      isDocumentAwaitingApprovalLoading,
      isInitiatedAcknowledgementsLoading,
      isReceivedAcknowledgementsLoading,
      userHasAccessToAdditionalTabs,
      isUnresolvedFeedbackLoading,
      initiatedAcknowledgements,
      receivedAcknowledgements,
    ]
  );
};
